import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
  FiBell, FiMessageSquare, FiSearch, FiSend, FiArrowLeft, FiCheck, FiTrash2, FiCircle,
} from "react-icons/fi";
import { getNotificationTypeConfig } from "../utils/notificationTypes";
import { useConfirm } from "../context/ConfirmContext";
import AppShell from "../components/layout/AppShell";
import { AuthContext } from "../context/AuthContext";
import { NotificationContext } from "../context/NotificationContext";
import { Avatar, Card, Spinner, EmptyState, Tabs } from "../components/ui";
import { getUsers } from "../api/users";
import { getConversations, getMessagesWith, sendMessageTo } from "../api/directMessages";
import { formatSmartTimestamp, groupByRecency } from "../utils/format";

// Each notification type gets a distinct icon + color + a human label —
// replaces showing the raw type string ("community_message") as an
// uppercase text pill, which reads as a technical tag rather than a
// notification.

export default function Messages() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const confirm = useConfirm();
  const {
    notifications,
    unreadCount,
    markRead,
    markOne,
    markUnread,
    deleteOne,
    deleteAll,
    markCategoryRead,
    clearDmUnread,
    subscribeToDirectMessages,
  } = useContext(NotificationContext);

  const [searchParams] = useSearchParams();
  const deepLinkPartnerId = searchParams.get("with");

  const [tab, setTab] = useState(deepLinkPartnerId ? "messages" : "inbox");
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [msgsLoading, setMsgsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const activeConvoRef = useRef(null);

  // Kept in a ref (not just state) so the SSE subscriber below — set up
  // once — always sees which conversation is currently open, without
  // needing to resubscribe every time the user switches threads.
  useEffect(() => {
    activeConvoRef.current = activeConvo;
  }, [activeConvo]);

  const loadUsers = useCallback(() => {
    getUsers().then((res) => setUsers((res.data?.data || []).filter((u) => u.id !== user?.id))).catch(() => {});
  }, [user?.id]);

  const loadConversations = useCallback(() => {
    getConversations().then((res) => setConversations(res.data?.conversations || [])).catch(() => {});
  }, []);

  // One-time load on mount — no interval here. Live updates for both
  // notifications and direct messages arrive via the single shared SSE
  // connection in NotificationContext, not by re-fetching on a timer.
  // (Re-fetching a whole list on a short timer and replacing state
  // wholesale is what caused the flicker before — a new array reference
  // every few seconds forces a full re-render even when nothing changed.)
  useEffect(() => {
    loadUsers();
    loadConversations();
  }, [loadUsers, loadConversations]);

  // Deep-linked from a notification (e.g. application feedback) — once the
  // user directory has loaded, jump straight into that conversation.
  useEffect(() => {
    if (deepLinkPartnerId && users.length > 0) {
      const partner = users.find((u) => String(u.id) === deepLinkPartnerId);
      if (partner) setActiveConvo(partner);
    }
  }, [deepLinkPartnerId, users]);

  // Opening the Inbox clears its sidebar badge and the DM badge — matches
  // every other section (Jobs, Communities, Courses, Admin).
  useEffect(() => {
    markCategoryRead("messages");
    clearDmUnread();
  }, [markCategoryRead, clearDmUnread]);

  const loadMessages = useCallback(async (partnerId) => {
    if (!partnerId) return;
    setMsgsLoading(true);
    try {
      const res = await getMessagesWith(partnerId);
      setMessages(res.data?.messages || []);
    } catch (e) {
      /* silent */
    } finally {
      setMsgsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeConvo) loadMessages(activeConvo.id);
  }, [activeConvo, loadMessages]);

  // Live incoming DMs for whichever conversation is open — pushed over the
  // shared SSE connection, not polled.
  useEffect(() => {
    const unsubscribe = subscribeToDirectMessages((incoming) => {
      const openId = activeConvoRef.current?.id;
      const involvesOpenThread = openId && (incoming.senderId === openId || incoming.recipientId === openId);
      if (involvesOpenThread) {
        setMessages((prev) => (prev.some((m) => m.id === incoming.id) ? prev : [...prev, incoming]));
      }
      // Keep the conversation list preview/order fresh regardless of which
      // thread is open.
      loadConversations();
    });
    return unsubscribe;
  }, [subscribeToDirectMessages, loadConversations]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!draft.trim() || !activeConvo) return;
    const text = draft.trim();
    setDraft("");
    setSending(true);
    const optimistic = { id: `temp-${Date.now()}`, senderId: user.id, recipientId: activeConvo.id, content: text, createdAt: new Date().toISOString() };
    setMessages((prev) => [...prev, optimistic]);
    try {
      const res = await sendMessageTo(activeConvo.id, text);
      setMessages((prev) => prev.map((m) => (m.id === optimistic.id ? res.data.message : m)));
      loadConversations();
    } catch {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
    } finally {
      setSending(false);
    }
  };

  const openConversation = (partner) => {
    setActiveConvo(partner);
  };

  const filteredUsers = users.filter((u) =>
    !searchQuery || `${u.name} ${u.lastName || ""}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Likes/reposts/saves/comments are now real, persisted notifications
  // (pushed live over SSE) rather than a separately-fetched derived feed,
  // so this is just the one notifications list, already sorted newest-first
  // by the backend.
  const allInbox = notifications;
  const groupedInbox = groupByRecency(allInbox, (n) => n.createdAt);

  const handleInboxClick = (n) => {
    markOne(n.id);
    if (!n.link) return;
    // Links pointing back at this same page (a DM, or a "with" partner)
    // switch tab/conversation directly instead of a full navigate — smoother
    // since we're already here, and avoids relying on the URL-driven effect
    // re-firing correctly for a same-page navigation.
    const withMatch = n.link.match(/^\/messages\?with=(\d+)/);
    if (withMatch) {
      const partnerId = withMatch[1];
      const partner = users.find((u) => String(u.id) === partnerId);
      setTab("messages");
      if (partner) setActiveConvo(partner);
      return;
    }
    if (n.link.startsWith("/messages")) {
      setTab("messages");
      return;
    }
    navigate(n.link);
  };

  const tabs = [
    { value: "inbox", label: "Inbox", icon: <FiBell className="h-4 w-4" />, count: unreadCount || undefined },
    { value: "messages", label: "Messages", icon: <FiMessageSquare className="h-4 w-4" /> },
  ];

  return (
    <AppShell title="Inbox" subtitle="Notifications and direct messages" wide>
      <Tabs tabs={tabs} active={tab} onChange={setTab} className="mb-6" />

      {tab === "inbox" && (
        <Card className="overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
            <p className="text-sm font-semibold text-slate-900">All activity</p>
            <div className="flex items-center gap-4">
              {unreadCount > 0 && (
                <button onClick={markRead} className="text-xs font-medium text-primary-600 hover:text-primary-700">
                  Mark all read
                </button>
              )}
              {allInbox.length > 0 && (
                <button
                  onClick={async () => {
                    const ok = await confirm({ title: "Clear all notifications?", description: "Every notification in your activity feed will be permanently removed. This cannot be undone." });
                    if (ok) deleteAll();
                  }}
                  className="text-xs font-medium text-slate-400 hover:text-red-600"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
          {allInbox.length === 0 ? (
            <EmptyState className="border-none" icon={<FiBell className="h-6 w-6" />} title="You're all caught up" description="Notifications about your activity will appear here." />
          ) : (
            <div>
              {groupedInbox.map(([section, items]) => (
                <div key={section}>
                  <p className="bg-slate-50 px-5 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                    {section}
                  </p>
                  <ul className="divide-y divide-slate-100">
                    {items.map((n) => {
                      const config = getNotificationTypeConfig(n.type);
                      const Icon = config.icon;
                      const unread = !n.read;
                      return (
                        <li
                          key={n.id}
                          onClick={() => handleInboxClick(n)}
                          className={`group flex items-center gap-3 px-5 py-3.5 cursor-pointer transition-colors hover:bg-slate-50 ${unread ? "bg-primary-50/40" : ""}`}
                        >
                          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${config.color}`}>
                            <Icon className="h-4 w-4" />
                          </div>

                          <div className="min-w-0 flex-1">
                            <p className={`truncate text-sm ${unread ? "font-semibold text-slate-900" : "font-normal text-slate-600"}`}>
                              {n.title}
                            </p>
                            {n.message && (
                              <p className="truncate text-xs text-slate-400">{n.message}</p>
                            )}
                          </div>

                          <span className="shrink-0 text-xs text-slate-400">{formatSmartTimestamp(n.createdAt)}</span>

                          {/* Unread signal: exactly one — a small dot. Not
                              stacked with border colors, background tints,
                              and badge-color changes all encoding the same
                              boolean. */}
                          {unread && <span className="h-2 w-2 shrink-0 rounded-full bg-primary-500" title="Unread" />}

                          {/* Row actions: always visible (not a hover-only
                              opacity) so this works the same on touch
                              devices where hover doesn't reliably exist. */}
                          <div className="flex shrink-0 items-center gap-0.5 pl-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); unread ? markOne(n.id) : markUnread(n.id); }}
                              className="rounded-md p-1.5 text-slate-300 hover:bg-slate-200 hover:text-slate-700"
                              title={unread ? "Mark as read" : "Mark as unread"}
                            >
                              {unread ? <FiCheck className="h-4 w-4" /> : <FiCircle className="h-4 w-4" />}
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteOne(n.id); }}
                              className="rounded-md p-1.5 text-slate-300 hover:bg-red-50 hover:text-red-600"
                              title="Delete"
                            >
                              <FiTrash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "messages" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Card className="overflow-hidden lg:col-span-1">
            <div className="border-b border-slate-100 p-3">
              <div className="relative">
                <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search members…"
                  className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>
            {conversations.length > 0 && !searchQuery && (
              <div className="border-b border-slate-100 px-3 py-2">
                <p className="px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Recent</p>
              </div>
            )}
            <ul className="overflow-y-auto divide-y divide-slate-100" style={{ maxHeight: "460px" }}>
              {searchQuery ? (
                filteredUsers.length === 0 ? (
                  <li className="p-6 text-center text-sm text-slate-400">No members found</li>
                ) : (
                  filteredUsers.map((u) => (
                    <ConvoRow key={u.id} person={u} active={activeConvo?.id === u.id} onClick={() => openConversation(u)} />
                  ))
                )
              ) : conversations.length === 0 ? (
                <li className="p-6 text-center text-sm text-slate-400">Search for a member to start chatting</li>
              ) : (
                conversations.map((c) => (
                  <ConvoRow
                    key={c.partner.id}
                    person={c.partner}
                    preview={c.lastMessage?.content}
                    unread={c.unreadCount}
                    active={activeConvo?.id === c.partner.id}
                    onClick={() => openConversation(c.partner)}
                  />
                ))
              )}
            </ul>
          </Card>

          <Card className="flex flex-col lg:col-span-2">
            {!activeConvo ? (
              <div className="flex flex-1 items-center justify-center p-12">
                <EmptyState className="border-none" icon={<FiMessageSquare className="h-6 w-6" />} title="Select a member to chat" description="Choose someone to start or continue a conversation." />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3">
                  <button onClick={() => setActiveConvo(null)} className="text-slate-400 hover:text-slate-600 lg:hidden">
                    <FiArrowLeft className="h-5 w-5" />
                  </button>
                  <Avatar name={`${activeConvo.name} ${activeConvo.lastName || ""}`} size="sm" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{activeConvo.name} {activeConvo.lastName}</p>
                    <Link to={`/profile/${activeConvo.id}`} className="text-xs text-primary-600 hover:text-primary-700">View profile</Link>
                  </div>
                </div>

                {/* Messages, then the input directly below — not fixed to the viewport. */}
                <div className="flex-1 space-y-3 overflow-y-auto p-4 scrollbar-thin" style={{ minHeight: "320px", maxHeight: "420px" }}>
                  {msgsLoading ? (
                    <div className="flex h-full items-center justify-center"><Spinner /></div>
                  ) : messages.length === 0 ? (
                    <EmptyState className="border-none" icon={<FiMessageSquare className="h-5 w-5" />} title="No messages yet" description="Send the first message!" />
                  ) : (
                    messages.map((m) => {
                      const mine = m.senderId === user?.id;
                      // senderLabel overrides the display name for specific
                      // messages (e.g. job-application feedback shows
                      // "Admin" rather than exposing which admin sent it) —
                      // it doesn't change who the conversation itself is
                      // with, just how that one message is labeled.
                      const displayName = m.senderLabel || (mine ? user?.name : activeConvo.name);
                      return (
                        <div key={m.id} className={`flex items-end gap-2 ${mine ? "flex-row-reverse" : ""}`}>
                          <Avatar name={displayName} size="xs" />
                          <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${mine ? "rounded-br-sm bg-primary-600 text-white" : "rounded-bl-sm bg-slate-100 text-slate-800"}`}>
                            {m.senderLabel && !mine && (
                              <p className="mb-0.5 text-[11px] font-semibold text-primary-600">{m.senderLabel}</p>
                            )}
                            <p className="whitespace-pre-wrap">{m.content}</p>
                            <p className={`mt-1 text-[10px] ${mine ? "text-primary-200" : "text-slate-400"}`}>{formatSmartTimestamp(m.createdAt)}</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>
                <div className="flex items-center gap-2 border-t border-slate-100 p-3">
                  <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), handleSend())}
                    placeholder="Type a message…"
                    className="h-10 flex-1 rounded-full border border-slate-200 bg-white px-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  />
                  <button
                    onClick={handleSend}
                    disabled={sending || !draft.trim()}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    <FiSend className="h-4 w-4" />
                  </button>
                </div>
              </>
            )}
          </Card>
        </div>
      )}
    </AppShell>
  );
}

function ConvoRow({ person, preview, unread, active, onClick }) {
  return (
    <li>
      <button onClick={onClick} className={`flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-slate-50 ${active ? "bg-primary-50" : ""}`}>
        <Avatar name={`${person.name} ${person.lastName || ""}`} size="md" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-800">{person.name} {person.lastName}</p>
          <p className="truncate text-xs text-slate-400">{preview || person.email}</p>
        </div>
        {unread > 0 && <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-500 text-[10px] font-bold text-white">{unread}</span>}
      </button>
    </li>
  );
}
